import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// Models
import { MetaData, PaginatedData, ResponseData } from 'src/app/platform/models/utils';
import { ProceduresData, AreaData, ReasonData } from 'src/app/modules/procedures/models/procedures';

// Service
import { UserService } from 'src/app/modules/account/user.service';
import { ApiService } from 'src/app/platform/services/api.service';
import { dateFormat, objectToString } from 'src/app/platform/utils/functions';

/**
 * This class handles all the actions related to the management
 */
@Injectable({
  providedIn: 'root'
})
export class ProceduresService {
  public proceduresIsUpdate = new BehaviorSubject<boolean>(false);

  constructor(
    private apiService: ApiService,
    private userService: UserService
  ) { }

  /**
   * Execute the process of reloading the list of procedures
   */
  proceduresUpdate(status: boolean): void {
    this.proceduresIsUpdate.next(status);
  }

  /**
   * Obtains a list of procedures
   */
  proceduresGet(page?: number | null, limit?: number | null, filters: any | null = null): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      let data: any = {page, limit};

      if (filters !== null) {
        data = {
          page,
          limit,
          estados: filters.status,
          fechaIngresoDesde: dateFormat(filters.dateStart.start, 'YYYY/MM/DD 00:00:00'),
          fechaIngresoHasta: dateFormat(filters.dateStart.end, 'YYYY/MM/DD 23:59:59'),
          fechaFinalizacionDesde: dateFormat(filters.dateEnd.start, 'YYYY/MM/DD 00:00:00'),
          fechaFinalizacionHasta: dateFormat(filters.dateEnd.end, 'YYYY/MM/DD 23:59:59'),
        };
      }

      this.apiService.request('get', 'gestiones/' + this.userService.currentUser.value.client + objectToString(data), {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const proceduresData: Array<ProceduresData> = [];

          success.items.forEach((value: any, index: number) => {
            proceduresData.push(new ProceduresData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', new PaginatedData(proceduresData, success.totalRegistros, success.limiteRegistros, success.paginas, success.paginaActual)));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Generate a new management
   */
  proceduresNew(area: number, reason: number, message: string): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      const data = {
        areaId: area,
        motivoId: reason,
        descripcion: message
      };

      this.apiService.request('post', 'gestiones/' + this.userService.currentUser.value.client, data, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          if (typeof success.id !== 'undefined' && success.id > 0){
            resolve(this.apiService.successDataPrepare('', success));
          } else {
            reject(this.apiService.errorDataPrepare('Hubo un inconveniente al intentar generar la gestión. Estamos trabajando para solucionar el inconveniente.', success));
          }
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Obtains a list of status of procedures
   */
   proceduresStatusGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'gestiones/' + this.userService.currentUser.value.client + '/estados', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const responseData: Array<MetaData> = [];

          success.estados.forEach((value: any, index: number) => {
            responseData.push(new MetaData(value, value));
          });

          resolve(this.apiService.successDataPrepare('', responseData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Obtains a list of areas to generate a management
   */
  areasGet(): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'gestiones/areas', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const areasData: Array<AreaData> = [];

          success.items.forEach((value: any, index: number) => {
            areasData.push(new AreaData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', areasData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }

  /**
   * Obtains a list of reasons to generate a management
   */
  reasonsGet(reason: number): Promise<ResponseData> {
    return new Promise((resolve, reject) => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Bearer ' + this.userService.currentUser.value.token
      };

      this.apiService.request('get', 'gestiones/areas/' + reason + '/motivos', {}, headers).toPromise().then((success: any) => {
        if (success.response.status) {
          const reasonsData: Array<ReasonData> = [];

          success.items.forEach((value: any, index: number) => {
            reasonsData.push(new ReasonData().configData(value));
          });

          resolve(this.apiService.successDataPrepare('', reasonsData));
        } else {
          reject(this.apiService.errorDataPrepare(success.response.message, success));
        }
      }, (error) => {
        reject(this.apiService.errorDataPrepare(error.response.message, error));
      });
    });
  }
}
